<template>
  <div class="doctor-documents-plate">
    <button
      v-if="hasContent"
      type="button"
      class="doctor-documents-plate__button align-center"
      :class="[plateClass, { 'doctor-documents-plate__button--desktop': !isMobile }]"
      data-qa="lpu_doctors_list_documents"
      @click="onPlateClick"
    >
      <div class="text-left mr-2 min-w-0">
        <div
          v-if="experienceLabel"
          class="text-subtitle-1"
        >
          {{ tStatic('Стаж') }} {{ experienceLabel }}
        </div>
        <div v-if="categoryLabel || scienceLabel">
          <span
            v-if="categoryLabel"
            class="text-body-2 doctor-documents-plate__dot"
          >
            {{ categoryLabel }} {{ tStatic('категория') }}
          </span>
          <span
            v-if="scienceLabel"
            class="text-body-2 doctor-documents-plate__dot"
          >
            {{ scienceLabel }}
          </span>
        </div>
        <div
          v-if="doctor.has_trust_sign && hasFakeDocuments"
          class="text-body-2 text-error--text"
        >
          {{ tStatic('Факт выдачи части документов опровергнут') }}
        </div>
        <div
          v-else-if="doctor.has_trust_sign"
          class="d-flex align-center text-body-2"
        >
          <span class="ui-icon-shield-check ui-icon-fz-smaller mr-1" />
          {{ tStatic('Документы проверены') }}
        </div>
      </div>
      <div
        v-if="doctor.has_trust_sign"
        class="ui-icon-arrow-right ui-icon-fz-big ml-auto flex-shrink-0"
      />
    </button>
    <DoctorDocumentsModal
      v-if="doctor.has_trust_sign"
      v-model="isDocumentsOpen"
      :documents="documentsPayload"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import filterGetTextCategory from 'common/filters/filterGetTextCategory'
import filterGetTextScience from 'common/filters/filterGetTextScience'
import DoctorDocumentsModal from 'src/components/common/DoctorDocumentsModal/DoctorDocumentsModal.vue'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import getDoctorDocumentsPayload from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/functions/getDoctorDocumentsPayload'
import getExperienceLabel from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/functions/getExperienceLabel'
import type { LpuDoctor } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/types'

const props = defineProps<{
  doctor: LpuDoctor
  documentsUrl: string
}>()

const isMobile = useIsMobile()
const isDocumentsOpen = ref(false)
const documentsPayload = computed(() => (
  getDoctorDocumentsPayload(props.doctor, props.documentsUrl)
))
const hasFakeDocuments = computed(() => Boolean(props.doctor.fake_document_type_list?.length))
const hasContent = computed(() => (
  props.doctor.experience_data != null
  || Boolean(props.doctor.category_document?.category)
  || Boolean(props.doctor.science_data)
))
const experienceLabel = computed(() => {
  if (props.doctor.experience_data == null) {
    return ''
  }

  return getExperienceLabel(props.doctor.experience_data)
})
const categoryLabel = computed(() => {
  const category = props.doctor.category_document?.category

  if (!category) {
    return ''
  }

  if (props.doctor.has_trust_sign && !props.doctor.category_document?.is_valid) {
    return ''
  }

  return tStatic(filterGetTextCategory(category, false).toLowerCase())
})
const scienceLabel = computed(() => {
  if (!props.doctor.science_data) {
    return ''
  }

  return tStatic(filterGetTextScience(props.doctor.science_data, false))
})
const isCompactPlate = computed(() => (
  !props.doctor.has_trust_sign
  && !categoryLabel.value
  && !scienceLabel.value
))
const plateClass = computed(() => {
  const layoutClass = isCompactPlate.value ? 'd-inline-flex' : 'd-flex w-100'
  const interactiveClass = props.doctor.has_trust_sign
    ? 'doctor-documents-plate__button--interactive'
    : ''

  if (!props.doctor.has_trust_sign) {
    return `bg-secondary ${layoutClass} rounded px-3 py-2 ${interactiveClass}`.trim()
  }

  const radiusClass = isMobile.value ? 'rounded-lg' : 'rounded-15'

  if (hasFakeDocuments.value) {
    return `bg-error ${layoutClass} ${radiusClass} px-4 py-3 ${interactiveClass}`.trim()
  }

  return `bg-lpu-branding-color-background text-lpu-branding-color-text ${layoutClass} ${radiusClass} px-4 py-3 ${interactiveClass}`.trim()
})

function onPlateClick() {
  if (!props.doctor.has_trust_sign) {
    return
  }

  isDocumentsOpen.value = true
}
</script>

<style lang="scss" scoped>
.doctor-documents-plate {
  display: contents;

  &__button {
    border: none;
    cursor: default;
    appearance: none;
    text-align: left;

    &--desktop {
      max-width: 320px;
    }

    &--interactive {
      cursor: pointer;
    }
  }

  &__dot + &__dot::before {
    content: '·';
    margin: 0 6px;
  }
}
</style>
